/**
 * Product Detail Page — 「この商品と一緒に見られている商品」
 *
 * GA4 BQ Export 由来の coview レコメンドを Ping app から取得して
 * 商品詳細ページの p-related-products セクションに描画する。
 *
 * API: GET /apps/ping/products/coview?product_id=<id>&limit=<n>
 * 仕様: docs/api/products-coview.md (shopify-yokumoku repo)
 */

(function () {
  "use strict";

  const API_PATH = "/apps/ping/products/coview";
  const DEFAULT_LIMIT = 4;

  function escapeHtml(s) {
    if (s == null) return "";
    return String(s)
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#39;");
  }

  function formatPrice(value) {
    if (value == null) return "";
    return Number(value).toLocaleString("ja-JP") + "円";
  }

  function buildTagBadges(tags) {
    if (!tags) return "";
    const labels = [];
    if (tags.free_shipping) labels.push("オンライン送料無料");
    if (tags.wrapping) labels.push("ラッピング対応");
    if (tags.noshi) labels.push("のし対応");
    if (tags.social_gift) labels.push("eギフト対応");
    if (tags.ec_exclusive) labels.push("オンライン限定");
    if (tags.frozen) labels.push("冷凍");
    if (labels.length === 0) return "";
    return labels.map((l) => `<span class="c-tag">${escapeHtml(l)}</span>`).join("");
  }

  function buildCartButton(item) {
    var status = item.status;
    var url = escapeHtml(item.url);
    var isPreorder = item.sales_method === "予約販売";

    if (status === "available" && item.variant_id) {
      var cartLabel = isPreorder ? "予約商品をカートに入れる" : "カートに入れる";
      // セット商品(SKU が S 始まり)は構成商品をカートに入れる必要があるため data-setitems を付与する。
      // これが無いと modal-cart がセット本体を直接追加し最終ガードで PDP 誘導になる。
      // coview API が setitems を返した場合のみ付与（js_search.js / ec-ranking.liquid と同形式）。
      var setitemsAttr = "";
      var setitems = item.setitems;
      if (setitems && Array.isArray(setitems) && setitems.length > 0) {
        var setitemsCsv = setitems.map(function (si) {
          if (typeof si === "object" && si !== null && si.id != null) {
            var m = String(si.id).match(/(\d+)$/);
            var numId = m ? m[1] : String(si.id);
            var qty = si.quantity || 1;
            return numId + ":" + qty;
          }
          var ms = String(si).match(/(\d+)$/);
          return ms ? ms[1] + ":1" : String(si) + ":1";
        }).join(",");
        setitemsAttr = ` data-setitems="${escapeHtml(setitemsCsv)}"`;
      }
      return `<a href="#" class="c-btn__cart js-add-to-cart"` +
        ` data-product-id="${escapeHtml(item.variant_id)}"` +
        ` data-base-product-id="${escapeHtml(item.product_id)}"` +
        ` data-product-url="${url}"` +
        setitemsAttr +
        ` data-product-title="${escapeHtml(item.title)}">${cartLabel}</a>`;
    }
    if (status === "sale_not_started") {
      return `<a href="${url}" class="c-btn__cart c-btn__cart--period">${isPreorder ? "予約販売開始前" : "販売開始前"}</a>`;
    }
    if (status === "sale_ended") {
      var endedLabel = isPreorder ? "予約販売終了しました" : (item.tags && item.tags.ec_exclusive ? "オンライン販売終了しました" : "販売終了しました");
      return `<a href="${url}" class="c-btn__cart c-btn__cart--period">${endedLabel}</a>`;
    }
    if (status === "restock") {
      return `<a href="${url}" class="c-btn__cart c-btn__cart--restock">再入荷お知らせ</a>`;
    }
    if (status === "store_only") {
      return `<a href="${url}" class="c-btn__cart c-btn__cart--store">店舗限定</a>`;
    }
    if (status === "partial_online") {
      return `<a href="${url}" class="c-btn__cart c-btn__cart--period">一部オンライン限定</a>`;
    }
    if (status === "out_of_stock" || status === "unavailable") {
      return `<a href="${url}" class="c-btn__cart c-btn__cart--out-stock">ただいま品切れしております</a>`;
    }
    return `<a href="${url}" class="c-btn__cart">商品ページへ</a>`;
  }

  function renderCard(item) {
    const tagBadges = buildTagBadges(item.tags);
    const imageHtml = item.image
      ? `<img src="${escapeHtml(item.image)}" alt="${escapeHtml(item.title)}" loading="lazy">`
      : "";
    const discountAttrs = (item.price != null)
      ? ` data-discount-price="${Math.round(item.price)}" data-product-id="${escapeHtml(String(item.product_id))}"` +
        (item.variant_id ? ` data-variant-id="${escapeHtml(String(item.variant_id))}"` : "")
      : "";
    const priceHtml = item.price != null
      ? `<p class="c-product-card__price"${discountAttrs}>${escapeHtml(formatPrice(item.price))}<small>(税込)</small></p>`
      : "";
    return `
      <article class="c-grid__item">
        <div class="c-product-card">
          <a href="${escapeHtml(item.url)}" class="c-product-card__img">${imageHtml}</a>
          <div class="c-product-card__body">
            <div class="js-matchHeight-item">
              <a href="${escapeHtml(item.url)}" class="c-product-card__title">${escapeHtml(item.title)}</a>
              ${tagBadges ? `<div class="c-tags">${tagBadges}</div>` : ""}
              ${priceHtml}
            </div>
            ${buildCartButton(item)}
          </div>
        </div>
      </article>
    `;
  }

  function init() {
    const node = document.querySelector("[data-coview-recommendations]");
    if (!node) return;

    const productId = (node.dataset.productId || "").trim();
    if (!productId || !/^\d+$/.test(productId)) return;

    const limit = parseInt(node.dataset.limit || String(DEFAULT_LIMIT), 10);
    const target = node.querySelector("[data-coview-target]");
    if (!target) return;

    const url = `${API_PATH}?product_id=${encodeURIComponent(productId)}&limit=${encodeURIComponent(limit)}`;

    fetch(url, { credentials: "same-origin" })
      .then((res) => (res.ok ? res.json() : Promise.reject(new Error("HTTP " + res.status))))
      .then((data) => {
        if (!data || !Array.isArray(data.items) || data.items.length === 0) {
          // 何も表示せず終了(枠ごと非表示のまま)
          return;
        }
        target.innerHTML = data.items.map(renderCard).join("");
        if (window.DiscountPrice) window.DiscountPrice.apply(target);
        node.removeAttribute("hidden");

        // 既存の matchHeight 初期化を再キック(カードの高さ揃え)
        if (window.jQuery && typeof window.jQuery.fn.matchHeight === "function") {
          window.jQuery(target).find(".js-matchHeight-item").matchHeight();
        }

        // SP の slick slider を再 init (recently-viewed と同パターン)
        if (window.jQuery) {
          var $target = window.jQuery(target);
          if ($target.hasClass("slick-initialized")) {
            $target.slick("unslick");
          }
        }
        if (typeof window.checkProductSlider === "function") {
          window.checkProductSlider();
        }
      })
      .catch((err) => {
        // API 失敗時は枠ごと非表示のまま(回復可能性のあるエラーはコンソールにだけ残す)
        if (window.console && console.warn) {
          console.warn("[coview] fetch failed:", err && err.message);
        }
      });
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();
